import React from "react";
import { Reveal, SectionHeading } from "./_shared";
import {
    Bot, FileText,
    Building2, ClockAlert, ShieldCheck, GraduationCap, Stethoscope,
    ClipboardList, UtensilsCrossed, Archive, Calendar, Mail, BarChart3,
} from "lucide-react";

// Prominent hero-features (AI + registri digitali)
const primary = [
    {
        icon: Bot,
        title: "Il tuo agente AI per la sicurezza aziendale",
        text: "Interroga la piattaforma in modo semplice, recupera informazioni, consulta scadenze e documenti e ottieni supporto nella gestione quotidiana attraverso il chatbot integrato.",
        pill: "Consulenza agentica",
    },
    {
        icon: FileText,
        title: "Registri completamente digitalizzati",
        text: "Compila, consulta e conserva registrazioni, documenti e dossier senza dover ricorrere a registri cartacei.",
        pill: "Elimina la carta",
    },
];

const secondary = [
    { icon: Building2, tone: "sky", title: "Aziende, sedi e lavoratori", text: "Organizza in modo strutturato le informazioni aziendali." },
    { icon: ClockAlert, tone: "peach", title: "Scadenze", text: "Individua ciò che è valido, prossimo alla scadenza o già scaduto." },
    { icon: ShieldCheck, tone: "cream", title: "Sicurezza sul lavoro", text: "Raccogli e consulta gli adempimenti collegati alla sicurezza." },
    { icon: GraduationCap, tone: "mint", title: "Formazione (compresa alimentaristi)", text: "Monitora corsi, attestati, aggiornamenti e formazione alimentaristi dei lavoratori." },
    { icon: Stethoscope, tone: "peach", title: "Sorveglianza sanitaria", text: "Tieni sotto controllo visite mediche e relativi adempimenti." },
    { icon: ClipboardList, tone: "sky", title: "Sopralluoghi", text: "Programma e consulta le attività effettuate presso le aziende." },
    { icon: UtensilsCrossed, tone: "mint", title: "HACCP", text: "Organizza gli adempimenti e le attività relative all'igiene alimentare." },
    { icon: Archive, tone: "lav", title: "DPI e dotazioni", text: "Gestisci registri, dotazioni e informazioni collegate." },
    { icon: Calendar, tone: "cream", title: "Calendario", text: "Visualizza attività, appuntamenti e scadenze." },
    { icon: Mail, tone: "sky", title: "Comunicazioni e mailing", text: "Gestisci comunicazioni verso i lavoratori e i responsabili." },
    { icon: BarChart3, tone: "mint", title: "Report", text: "Ottieni una panoramica più chiara delle attività." },
];

const bg = { sky: "#E4ECFD", peach: "#FDE3DD", cream: "#FBF3D4", mint: "#DFF3E4", lav: "#EDE3FD" };
const fg = { sky: "#1B4CE0", peach: "#B12D2D", cream: "#8C6E14", mint: "#0E8E4E", lav: "#5F32C4" };

export default function Features() {
    return (
        <section id="funzionalita" className="relative py-24 md:py-32 bg-white">
            <div className="max-w-[1400px] mx-auto px-5 md:px-8">
                <SectionHeading
                    chapter={3}
                    chapterLabel="Funzionalità"
                    title={
                        <>
                            Una piattaforma pensata per la <em className="not-italic bg-gold-200 px-2">gestione quotidiana</em>,
                            <br className="hidden md:block" /> utilizzabile anche con il tuo <span className="text-gold-500">agente AI</span>.
                        </>
                    }
                />
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-2xl text-navy-600 text-lg leading-relaxed">
                        Un gestionale sicurezza sul lavoro completo: due elementi differenziano SafetyDesk81 da
                        qualunque scadenzario — l'intelligenza artificiale e i registri digitali. A supporto, tutte le
                        aree HSE necessarie alla gestione quotidiana, dalla formazione alimentaristi ai sopralluoghi.
                    </p>
                </Reveal>

                {/* Primary highlighted cards */}
                <div className="mt-14 grid md:grid-cols-2 gap-5">
                    {primary.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <Reveal key={p.title} delay={i * 0.1}>
                                <article
                                    data-testid={`feature-primary-${i}`}
                                    className="relative h-full rounded-3xl p-8 md:p-10 bg-navy-950 text-white overflow-hidden hover-lift border border-navy-800"
                                >
                                    <div aria-hidden className="absolute -top-16 -right-16 w-64 h-64 bg-gold-400/20 blur-3xl rounded-full" />
                                    <div className="relative">
                                        <div className="flex items-center justify-between mb-6">
                                            <div className="w-14 h-14 rounded-2xl bg-gold-400 text-navy-950 flex items-center justify-center">
                                                <Icon size={26} strokeWidth={2} />
                                            </div>
                                            <span className="text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-full bg-white/10 text-gold-300 border border-white/10">
                                                {p.pill}
                                            </span>
                                        </div>
                                        <h3 className="font-display font-black text-white text-2xl md:text-3xl leading-tight">{p.title}</h3>
                                        <p className="mt-4 text-white/70 leading-relaxed">{p.text}</p>
                                    </div>
                                </article>
                            </Reveal>
                        );
                    })}
                </div>

                {/* Secondary cards */}
                <Reveal delay={0.2}>
                    <div className="mt-16 flex items-center gap-3 text-navy-400 text-[11px] font-mono uppercase tracking-widest">
                        <span className="h-px flex-1 bg-navy-100" />
                        <span>Tutte le altre sezioni</span>
                        <span className="h-px flex-1 bg-navy-100" />
                    </div>
                </Reveal>
                <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {secondary.map((it, i) => {
                        const Icon = it.icon;
                        return (
                            <Reveal key={it.title} delay={0.05 * (i % 6)}>
                                <div
                                    data-testid={`feature-${i}`}
                                    className="group h-full rounded-3xl p-6 md:p-7 bg-white border border-navy-100 hover-lift transition-all relative overflow-hidden"
                                >
                                    <div
                                        className="w-11 h-11 rounded-2xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110 group-hover:-rotate-3"
                                        style={{ background: bg[it.tone], color: fg[it.tone] }}
                                    >
                                        <Icon size={20} strokeWidth={1.8} />
                                    </div>
                                    <h3 className="font-display font-bold text-navy-950 text-lg leading-tight">{it.title}</h3>
                                    <p className="mt-2 text-navy-600 text-[14px] leading-relaxed">{it.text}</p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
